import React, { useEffect, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { Input, Select } from '../../components/ui/Input';
import { contasReceberApi } from '../../api/financeiro';
import { clientesTenantApi } from '../../api/clientes';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

const FORMA_LABEL = { boleto: 'Boleto', pix: 'Pix', conta: 'Crédito em Conta' };
const STATUS_TOM = { pendente: 'aviso', recebido: 'sucesso', cancelado: 'perigo' };
const STATUS_LABEL = { pendente: 'Pendente', recebido: 'Recebido', cancelado: 'Cancelado' };
const CATEGORIA_LABEL = { venda_mercadorias: 'Venda de Mercadorias', servicos: 'Serviços', outros: 'Outros' };
const OPCOES_CATEGORIA = [
  { value: 'venda_mercadorias', label: 'Venda de Mercadorias' },
  { value: 'servicos', label: 'Serviços' },
  { value: 'outros', label: 'Outros' }
];

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function estaAtrasada(conta) {
  return conta.status === 'pendente' && conta.data_vencimento < new Date().toISOString().slice(0, 10);
}

const FORM_VAZIO = {
  descricao: '', categoria: 'venda_mercadorias', cliente_id: '', forma_recebimento: 'boleto',
  valor: '', data_emissao: new Date().toISOString().slice(0, 10), data_vencimento: '', observacoes: '',
  recorrente: false, recorrencia_meses: '12'
};

export default function ContasReceberList() {
  useTituloPagina('Contas a Receber');
  const [contas, setContas] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [filtroStatus, setFiltroStatus] = useState('pendente');
  const [filtroForma, setFiltroForma] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const [filtroDataDe, setFiltroDataDe] = useState('');
  const [filtroDataAte, setFiltroDataAte] = useState('');
  const [filtroBusca, setFiltroBusca] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [prazosBoleto, setPrazosBoleto] = useState(['30']);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = () => {
    const params = {};
    if (filtroStatus) params.status = filtroStatus;
    if (filtroForma) params.forma_recebimento = filtroForma;
    if (filtroCategoria) params.categoria = filtroCategoria;
    if (filtroCliente) params.cliente_id = filtroCliente;
    if (filtroOrigem) params.origem = filtroOrigem;
    if (filtroDataDe) params.data_de = filtroDataDe;
    if (filtroDataAte) params.data_ate = filtroDataAte;
    if (filtroBusca) params.busca = filtroBusca;
    contasReceberApi.listar(params).then(setContas).catch(() => {});
  };

  useEffect(() => { carregar(); }, [filtroStatus, filtroForma, filtroCategoria, filtroCliente, filtroOrigem, filtroDataDe, filtroDataAte, filtroBusca]); // eslint-disable-line
  useEffect(() => { clientesTenantApi.listar().then(setClientes).catch(() => {}); }, []);

  const totalListado = contas.reduce((s, c) => s + Number(c.valor), 0);
  const opcoesClientes = [{ value: '', label: '(sem cliente)' }, ...clientes.map(c => ({ value: c.id, label: c.nome_fantasia }))];
  const opcoesClientesFiltro = [{ value: '', label: 'Todos os clientes' }, ...clientes.map(c => ({ value: c.id, label: c.nome_fantasia }))];

  const abrirNovo = () => { setForm(FORM_VAZIO); setPrazosBoleto(['30']); setErro(''); setModalAberto(true); };

  const limparFiltros = () => {
    setFiltroStatus(''); setFiltroForma(''); setFiltroCategoria(''); setFiltroCliente('');
    setFiltroOrigem(''); setFiltroDataDe(''); setFiltroDataAte(''); setFiltroBusca('');
  };

  const alterarPrazoBoleto = (idx, valor) => setPrazosBoleto(p => p.map((v, i) => i === idx ? valor : v));
  const adicionarPrazoBoleto = () => setPrazosBoleto(p => [...p, '']);
  const removerPrazoBoleto = (idx) => setPrazosBoleto(p => p.filter((_, i) => i !== idx));

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');
    const payload = {
      descricao: form.descricao, categoria: form.categoria, cliente_id: form.cliente_id || null,
      forma_recebimento: form.forma_recebimento, valor: form.valor, observacoes: form.observacoes
    };

    if (form.forma_recebimento === 'boleto') {
      const dias = prazosBoleto.map(d => d.trim()).filter(Boolean);
      if (dias.length === 0 || !dias.every(d => /^\d+$/.test(d))) {
        setErro('Preencha os prazos das parcelas com números de dias válidos.');
        return;
      }
      if (!form.data_emissao) { setErro('Informe a data de emissão.'); return; }
      payload.data_emissao = form.data_emissao;
      payload.parcelas_dias = dias.map(Number);
      if (dias.length === 1 && form.recorrente) {
        payload.recorrente = true;
        payload.recorrencia_meses = Number(form.recorrencia_meses) || 12;
      }
    } else {
      if (!form.data_vencimento) { setErro('Informe a data de vencimento.'); return; }
      payload.data_emissao = form.data_emissao || null;
      payload.data_vencimento = form.data_vencimento;
      if (form.recorrente) {
        payload.recorrente = true;
        payload.recorrencia_meses = Number(form.recorrencia_meses) || 12;
      }
    }

    setSalvando(true);
    try {
      await contasReceberApi.criar(payload);
      setModalAberto(false);
      carregar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  const marcarRecebido = async (conta) => {
    if (!window.confirm(`Confirmar recebimento de ${formatarReais(conta.valor)} — ${conta.descricao}?`)) return;
    await contasReceberApi.receber(conta.id);
    carregar();
  };

  const cancelar = async (conta) => {
    if (!window.confirm(`Cancelar o lançamento "${conta.descricao}"?`)) return;
    await contasReceberApi.cancelar(conta.id);
    carregar();
  };

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <strong style={{ fontSize: 13 }}>Filtros</strong>
          <Btn onClick={abrirNovo}>+ Novo Lançamento</Btn>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
          <Select label="Status" options={[{ value: '', label: 'Todos' }, { value: 'pendente', label: 'Pendente' }, { value: 'recebido', label: 'Recebido' }, { value: 'cancelado', label: 'Cancelado' }]}
            value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} />
          <Select label="Forma de Recebimento" options={[{ value: '', label: 'Todas' }, { value: 'boleto', label: 'Boleto' }, { value: 'pix', label: 'Pix' }, { value: 'conta', label: 'Crédito em Conta' }]}
            value={filtroForma} onChange={e => setFiltroForma(e.target.value)} />
          <Select label="Categoria" options={[{ value: '', label: 'Todas' }, ...OPCOES_CATEGORIA]}
            value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)} />
          <Select label="Origem" options={[{ value: '', label: 'Todas' }, { value: 'nota_fiscal', label: 'Nota Fiscal (Pedido)' }, { value: 'manual', label: 'Lançamento Manual' }]}
            value={filtroOrigem} onChange={e => setFiltroOrigem(e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <Select label="Cliente" options={opcoesClientesFiltro} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)} />
          <Input label="Vencimento De" type="date" value={filtroDataDe} onChange={e => setFiltroDataDe(e.target.value)} />
          <Input label="Vencimento Até" type="date" value={filtroDataAte} onChange={e => setFiltroDataAte(e.target.value)} />
          <Input label="Buscar Descrição" value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)} placeholder="Ex: NF 123..." />
          <Btn variante="ghost" onClick={limparFiltros}>Limpar</Btn>
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ background: COLORS.primaryBg, borderRadius: 10, padding: 14, flex: 1 }}>
            <div style={{ fontSize: 11, color: COLORS.primary, textTransform: 'uppercase', fontWeight: 700 }}>Total Listado</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{formatarReais(totalListado)}</div>
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 10, padding: 14, flex: 1 }}>
            <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>Lançamentos</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{contas.length}</div>
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', background: COLORS.bg }}>
              <th style={{ padding: 10 }}>Vencimento</th>
              <th style={{ padding: 10 }}>Descrição</th>
              <th style={{ padding: 10 }}>Categoria</th>
              <th style={{ padding: 10 }}>Cliente</th>
              <th style={{ padding: 10 }}>Forma</th>
              <th style={{ padding: 10 }}>Valor</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }} />
            </tr>
          </thead>
          <tbody>
            {contas.map(c => (
              <tr key={c.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: 10, color: estaAtrasada(c) ? COLORS.danger : COLORS.text, fontWeight: estaAtrasada(c) ? 700 : 400 }}>
                  {new Date(c.data_vencimento + 'T00:00:00').toLocaleDateString('pt-BR')}{estaAtrasada(c) ? ' (atrasada)' : ''}
                </td>
                <td style={{ padding: 10 }}>
                  {c.descricao}
                  {c.pedido_numero && <Badge tom="info">Pedido {c.pedido_numero}</Badge>}
                </td>
                <td style={{ padding: 10 }}>{CATEGORIA_LABEL[c.categoria] || '-'}</td>
                <td style={{ padding: 10 }}>{c.cliente_nome || '-'}</td>
                <td style={{ padding: 10 }}>{FORMA_LABEL[c.forma_recebimento]}</td>
                <td style={{ padding: 10, fontWeight: 600 }}>{formatarReais(c.valor)}</td>
                <td style={{ padding: 10 }}><Badge tom={STATUS_TOM[c.status]}>{STATUS_LABEL[c.status]}</Badge></td>
                <td style={{ padding: 10 }}>
                  {c.status === 'pendente' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn tamanho="sm" onClick={() => marcarRecebido(c)}>Receber</Btn>
                      <Btn tamanho="sm" variante="danger" onClick={() => cancelar(c)}>Cancelar</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {contas.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhum lançamento encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {modalAberto && (
        <Modal titulo="Novo Lançamento" onFechar={() => setModalAberto(false)} largura={560}
          rodape={<>
            <Btn variante="ghost" onClick={() => setModalAberto(false)}>Cancelar</Btn>
            <Btn onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</Btn>
          </>}>
          <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="Descrição" value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))} required />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Select label="Categoria" options={OPCOES_CATEGORIA} value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} />
              <Select label="Cliente (opcional)" options={opcoesClientes} value={form.cliente_id} onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value }))} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Select label="Forma de Recebimento" options={[{ value: 'boleto', label: 'Boleto' }, { value: 'pix', label: 'Pix' }, { value: 'conta', label: 'Crédito em Conta' }]}
                value={form.forma_recebimento} onChange={e => setForm(f => ({ ...f, forma_recebimento: e.target.value }))} />
              <Input label="Valor Total R$" type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} required />
            </div>

            <Input label="Data de Emissão" type="date" value={form.data_emissao} onChange={e => setForm(f => ({ ...f, data_emissao: e.target.value }))} />

            {form.forma_recebimento === 'boleto' ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>
                  Parcelas (dias após a data de emissão)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {prazosBoleto.map((prazo, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Input type="number" min="0" value={prazo} onChange={e => alterarPrazoBoleto(idx, e.target.value)}
                        placeholder="Ex: 30" style={{ width: 120 }} />
                      <span style={{ fontSize: 12, color: COLORS.textLight }}>dias — vencimento calculado automaticamente</span>
                      {prazosBoleto.length > 1 && (
                        <Btn tamanho="sm" variante="danger" type="button" onClick={() => removerPrazoBoleto(idx)}>Remover</Btn>
                      )}
                    </div>
                  ))}
                </div>
                <Btn tamanho="sm" variante="secondary" type="button" onClick={adicionarPrazoBoleto} style={{ marginTop: 8 }}>
                  + Adicionar Parcela
                </Btn>
                <p style={{ fontSize: 11, color: COLORS.textLight, marginTop: 8 }}>
                  O valor total é dividido igualmente entre as parcelas (a última ajusta os centavos).
                </p>
              </div>
            ) : (
              <Input label="Data de Vencimento" type="date" value={form.data_vencimento} onChange={e => setForm(f => ({ ...f, data_vencimento: e.target.value }))} required />
            )}

            {(form.forma_recebimento !== 'boleto' || prazosBoleto.length === 1) && (
              <div style={{ background: COLORS.bg, borderRadius: 8, padding: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.recorrente} onChange={e => setForm(f => ({ ...f, recorrente: e.target.checked }))} />
                  Lançamento recorrente (ex: mensalidade de cliente)
                </label>
                {form.recorrente && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Input type="number" min="1" max="36" value={form.recorrencia_meses}
                      onChange={e => setForm(f => ({ ...f, recorrencia_meses: e.target.value }))} style={{ width: 90 }} />
                    <span style={{ fontSize: 12, color: COLORS.textLight }}>meses seguidos, repetindo no mesmo dia do mês</span>
                  </div>
                )}
              </div>
            )}

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, marginBottom: 4 }}>Observações</div>
              <textarea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} rows={2}
                style={{ width: '100%', border: `1.5px solid ${COLORS.border}`, borderRadius: 7, padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            {erro && <p style={{ color: COLORS.danger, fontSize: 13, margin: 0 }}>{erro}</p>}
          </form>
        </Modal>
      )}
    </div>
  );
}
